<template>
    <div class="sf__filters" v-bind:class="{
        'sf__filters--popup': popup, 
        'sf__filters--show': showFilters,
        'sf__filters--drawer-flow': isDrawerFlow()
    }">
        <a href="#" class="sf__filters__close" @click.prevent="closeFilters()">&times;</a>
        <div v-if="!started" class="sf__filters__inner">
            <Loader />
        </div>
        <div v-else class="sf__filters__inner">
            <button type="button" class="sf__filters__close__popup" @click="closePopup" aria-label="Close">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.0605 21L28.5 14.5605L27.4395 13.5L21 19.9395L14.5605 13.5L13.5 14.5605L19.9395 21L13.5 27.4395L14.5605 28.5L21 22.0605L27.4395 28.5L28.5 27.4395L22.0605 21Z" fill="black"/>
                </svg>
            </button>

            
            <template v-if="shouldDisplayFilters()">
            <!-- This button allows to reset the entire selection. -->
            <p v-if="!popup">
                <a href="#" class="sf__filters__edit" @click.prevent="reloadPage()">Edit Selection</a>
            </p>

            <template v-for="(section, index) in sections" :key="index">
                <FiltersSection
                    v-if="(popup && currentSectionIndex == index) || (!popup && section.show_after_popup)"
                    :filters="getSectionFilters(section.slug)"
                    :section="section"
                    :index="index"
                    :minIndex="firstSectionIndex"
                    :maxIndex="lastSectionIndex"
                    :totalSections="sections.length"
                    :selections="selections"
                    :stopped="stopped"
                    @select="(selection) => applySelection(selection, section)"
                    @removeOption="removeOption"
                    @nextSection="goToNextSection()"
                    @prevSection="goToPrevSection()"
                    @stopFilters="stopped = true"
                    :results="getFilteredResults(index)"
                    :alerts="alerts"
                    :foam="foam"
                    :popup="popup"
                    :drawerFlow="isDrawerFlow()"
                    :fullConfigurationBypassed="isFullConfigurationBypassed()"
                />
                <FiltersSummary
                    v-if="!popup && !section.show_after_popup"
                    :section="section"
                    :filters="getSectionFilters(section.slug)"
                    :selections="selections"
                    :foam="foam"
                    :alerts="alerts"
                />
            </template>

            </template>
        </div>
    </div>
</template>


<script>
import FiltersSection from './FiltersSection.vue'; 
import FiltersSummary from './FiltersSummary.vue'; 
import Filters from '../helpers/filters'; 
import Loader from './Loader.vue'; 
import store from '../store'; 
import Storage from '../helpers/storage'; 
import {getSingleOptionMessage} from '../helpers/filterAppMessages';

export default {

    props: [
        'results',
        'popup',
        'vehicle',
        'foam',
        'globalSelections',
        'showFilters',
        'drawerFlow',
        'initialStep',
        'preparedFilters',
        'fitmentBypassed',
        'fullConfigurationBypassed'
    ],

    components: {
        Loader, 
        FiltersSection, 
        FiltersSummary
    }, 

    data() {
        return {
            // Here the slug can be a single filter slug, or a section slug, that will pull all slugs related 
            // to that section. 
            sections: this.getSections(),
            selections: {},
            firstSectionIndex: 0, 
            lastSectionIndex: 0, 
            currentSectionIndex: 0, 
            filters: [],
            draftAlerts: {},
            started: false,
            ignored: [], 

            // This is to stop automatic filtering advance after the initial filtering loads. 
            // So on page load, filters advance continuously until one of them changes this flag. 
            // and this allows us to go back on filters and prevent theem from advancing again. 
            stopped: false 
        }
    }, 

    mounted() {
        this.selections = this.globalSelections; 
        this.init(); 
    }, 

    computed: {
        alerts() {
            return this.isDrawerFlow() ? this.draftAlerts : store.alerts;
        }
    },

    watch: {
        currentSectionIndex() {
            this.emitDrawerStep();
        },

        started() {
            this.emitDrawerStep();
        }
    },

    methods: {
        isDrawerFlow() {
            return this.drawerFlow === true || this.drawerFlow === 'true';
        },

        isFitmentBypassed() {
            return this.fitmentBypassed === true || this.fitmentBypassed === 'true';
        },

        isFullConfigurationBypassed() {
            return this.fullConfigurationBypassed === true
                || this.fullConfigurationBypassed === 'true';
        },

        getSections() {
            if (this.drawerFlow === true || this.drawerFlow === 'true') {
                return [
                    {
                        slug: ['cab_configuration', 'seat_configuration_1'],
                        title: 'Select fitment options:',
                        step: 'fitment',
                        nextLabel: 'Next: Material',
                        show_after_popup: false,
                        sequential: false
                    },
                    {
                        slug: 'part',
                        title: 'Select material options:',
                        step: 'material',
                        finalLabel: 'View Products',
                        show_after_popup: true,
                        sequential: false
                    }
                ];
            }

            return [
                {slug: 'cab_configuration', title: 'Cab Type', show_after_popup: false, sequential: true},
                {slug: 'seat_configuration_1', title: 'Seat Style', show_after_popup: false, sequential: true},
                {slug: 'part', title: 'Seat Options', show_after_popup: true, sequential: false}
            ];
        },

        emitDrawerStep() {
            if (!this.isDrawerFlow() || !this.started) return;

            const section = this.sections[this.currentSectionIndex];
            if (section?.step) {
                this.$emit('drawerStepChange', section.step, {
                    selections: {...this.selections}
                });
            }
        },

        async init() {

            const filters = this.preparedFilters || await (new Filters(this.results)).build({
                vehicle_id: this.vehicle.id 
            }); 

            // Drawer filters belong to the draft vehicle, not the visible PLP.
            if (!this.isDrawerFlow()) {
                store.filters = filters;
            }

            // For the initial filtering, we only pull those filters paired with vehicles or parts. 
            this.filters = filters.filter( filter => {
                return ['vehicle', 'part'].includes(filter.type); 
            }); 

            this.normalizeSelections();

            // Getting all sections with unfilled values. 
            let unfilled_sections = this.getUnfilledSectionIndexes(); 

            // With these values we know what section comes first, 
            // and what section comes last. 
            if ( unfilled_sections.length > 0 ) {
                this.currentSectionIndex = Math.min(...unfilled_sections); 
                this.firstSectionIndex = Math.min(...unfilled_sections);  
                this.lastSectionIndex = Math.max(...unfilled_sections); 
            } else {
                this.currentSectionIndex = this.firstAvailableSectionIndex(); 
                this.firstSectionIndex = this.currentSectionIndex;  
                this.lastSectionIndex = this.lastAvailableSectionIndex(); 
            }

            if (this.isDrawerFlow() && this.initialStep) {
                const requestedIndex = this.sections.findIndex(section => section.step === this.initialStep);

                if (requestedIndex >= 0) {
                    this.currentSectionIndex = requestedIndex;
                    this.firstSectionIndex = 0;
                    this.lastSectionIndex = Math.max(
                        requestedIndex,
                        this.sections.length - 1,
                        ...unfilled_sections
                    );
                }
            }

            this.started = true; 

            if (
                !this.isDrawerFlow()
                && unfilled_sections.length === 0
                && this.isValidSelection(this.filledSelections())
            ) {
                this.applyFilters(); 
            }
        },

        /**
         * URL-loaded selections can only skip the popup when they map to real,
         * applicable multi-choice filters. Empty or single-value filters are
         * informational, so they should not block direct PLP rendering.
         */
        normalizeSelections() {
            this.filters.forEach( filter => {
                let values = this.activeValues(filter);

                this.alerts[filter.slug] = ( values.length === 1)
                    ? {
                        label: filter.label || filter.slug.replace(/_/g, ' '), 
                        message: getSingleOptionMessage(values[0].value),
                        value: values[0].value,
                        image: values[0].image 
                    } : null;  

                if ( values.length <= 1 ) {
                    if ( this.selections[filter.slug] == null ) {
                        delete this.selections[filter.slug]; 
                    }

                    return; 
                }

                if ( !this.hasActiveSelection(filter, values) ) {
                    this.selections[filter.slug] = null; 
                }
            }); 
        },

        hasActiveSelection(filter, values) {
            return this.selections[filter.slug] != null
                && values.some( value => value.value == this.selections[filter.slug] ); 
        },

        filledSelections() {
            return Object.keys(this.selections).reduce( (selections, key) => {
                if ( this.selections[key] != null ) {
                    selections[key] = this.selections[key]; 
                }

                return selections; 
            }, {}); 
        },

        getUnfilledSectionIndexes() {
            return this.sections.map((section, index) => {
                return {
                    section: section, 
                    index: index // We do this to keep the original index 
                }; 
            }).filter( s => {
                return this.getSectionFilters(s.section.slug).some( f => {
                    return this.activeValues(f).length > 1
                        && this.selections[f.slug] == null; 
                })
            }).map( (section) => section.index ); 
        },

        getConfigurableSectionIndexes() {
            return this.sections.map((section, index) => {
                return {
                    section: section,
                    index: index
                };
            }).filter(s => {
                return this.getSectionFilters(s.section.slug).some(f => {
                    return this.activeValues(f).length > 1;
                });
            }).map(section => section.index);
        },

        firstAvailableSectionIndex() {
            const sections = this.getConfigurableSectionIndexes();
            return sections.length ? Math.min(...sections) : 0;
        },

        lastAvailableSectionIndex() {
            const sections = this.getConfigurableSectionIndexes();
            return sections.length ? Math.max(...sections) : 0;
        },

        shouldDisplayFilters() {
            // We only display filters if: 
            // - the popup is opened. 
            // - there are selections to show. 
            // - we're showing foam warnings. 
            return this.popup || Object.keys(this.selections).length > 0 || this.foam; 
        }, 

        /**
         * Gets the filters corresponding to a specific section. 
         */
        getSectionFilters(sectionName) {
            const sectionNames = Array.isArray(sectionName) ? sectionName : [sectionName];

            return this.filters.filter( filter => {
                return sectionNames.includes(filter.type)
                    || sectionNames.includes(filter.slug);
            }); 
        }, 


        /**
         * Applies the selection from the Section. 
         */
        applySelection(selection, section)
        {
            const {slug, value, force} = selection; 

            // 1. if the selection is valid, we just keep it. 
            // 2. if the selection is invalid, we should unselect all other values for that section and 
            // only leave the selected one, giving the customer the chance to reselect everything else again. 
            let desiredSelection = {
                ...this.selections, 
                ...{[slug]: value}
            }; 

            desiredSelection = this.clearInvalidDownstreamSelections(desiredSelection, slug, section);

            if ( force || this.isValidSelection( desiredSelection ) ) {
                this.selections = desiredSelection; 
            } else {
                if (!confirm('This option is unavailable for your current selection. Selecting this option will clear all selections.')) {
                    return false; 
                }

                // We clear all other section values except for this one. 
                this.getSectionFilters(section.slug).forEach( filter => {
                    if (typeof this.selections[filter.slug] === 'undefined') return; 

                    let values = this.activeValues(filter);
                    this.selections[filter.slug] = ( filter.slug == slug )
                        ? value
                        : (values.length > 1)
                            ? null
                            : values[0].value;  
                });
            }

            // if we're in the results view mode, we just apply filters 
            // after selecting. 
            if ( !this.popup ) this.applyFilters(); 
        }, 

        /**
         * Parent options in a section should stay selectable. When changing a
         * parent invalidates a later child option, clear only that stale child.
         */
        clearInvalidDownstreamSelections(selection, changedSlug, section) {
            const filters = this.getSectionFilters(section.slug);
            const changedIndex = filters.findIndex(filter => filter.slug == changedSlug);
            const sectionSlugs = filters.map(filter => filter.slug);

            if (changedIndex < 0) {
                return selection;
            }

            return filters.reduce((updatedSelection, filter, index) => {
                if (index <= changedIndex || updatedSelection[filter.slug] == null) {
                    return updatedSelection;
                }

                if (this.isValidSelection(
                    this.selectionThroughSectionFilter(updatedSelection, filters, sectionSlugs, index)
                )) {
                    return updatedSelection;
                }

                return {
                    ...updatedSelection,
                    [filter.slug]: null
                };
            }, selection);
        },

        selectionThroughSectionFilter(selection, filters, sectionSlugs, filterIndex) {
            const allowedSectionSlugs = filters
                .filter((filter, index) => index <= filterIndex)
                .map(filter => filter.slug);

            return Object.keys(selection).reduce((filteredSelection, key) => {
                if (!sectionSlugs.includes(key) || allowedSectionSlugs.includes(key)) {
                    filteredSelection[key] = selection[key];
                }

                return filteredSelection;
            }, {});
        },

        /**
         * This removes an item from the global selections, useful 
         * when we find that after filtering a specific option is no 
         * longer available. 
         */
        removeOption(selection) {
            delete this.selections[selection.slug]; 
        }, 

        /**
         * Verifies if a selection is valid. 
         */
        isValidSelection(selection) {
            return this.results.filter( result => {
                return ( this.foam ) ? result.isFoam() : true; 
            }).some( result => {
                for (const key in selection) {
                    if ( !selection[key] ) continue;

                    if ( !result.optionMatches(key, selection[key]) ) {
                        return false; 
                    }
                }

                return true; 
            }); 
        }, 

        applyFilters() {
            const selections = this.filledSelections();

            this.$emit('onApply', {
                selections,
                ignored: this.getIgnoredSelections()
            });
        }, 

        activeValues(filter) {
            return filter.values.filter( value => {
                return this.results.some( result => result.optionMatches(filter.slug, value.value) ); 
            });
        }, 

        allSelectionsMade() {
            return Object.values(this.selections).filter( val => val == null ).length === 0; 
        }, 

        goToNextSection() {
            // Drawer navigation must follow the visible step sequence even when
            // a later section already has saved selections from an edit flow.
            if (this.isDrawerFlow() && this.currentSectionIndex < this.sections.length - 1) {
                this.currentSectionIndex++;
                return;
            }

            if ( this.currentSectionIndex == this.lastSectionIndex ) {
                this.applyFilters(); 
            } else {
                this.currentSectionIndex++;
            }
        }, 

        goToPrevSection() {
            this.stopped = true;

            if (this.isDrawerFlow() && this.isFitmentBypassed()) {
                this.$emit('drawerBack');
                return;
            }

            if (this.isDrawerFlow() && this.currentSectionIndex == this.firstSectionIndex) {
                this.$emit('drawerBack');
                return;
            }

            this.currentSectionIndex--;
        }, 

        /**
         * This method gets filtered results for the given section
         */
        getFilteredResults(sectionIndex) {
            // For the first section we just return everything. 
            if ( sectionIndex === 0 ) return this.results; 
            let selections = {}; 

            // Let's get active selections for previous sections. 
            this.sections
                .filter ((section, index) => index < sectionIndex )
                .forEach( section => {
                    this.getSectionFilters(section.slug).forEach( filter => {
                        if ( this.selections[filter.slug] != null ) {
                            selections[filter.slug] = this.selections[filter.slug];
                        } 
                    }); 
                }); 

            // And now we only return relevant results that match all those previous selections. 
            return this.results.filter( result => {

                // If this is not a foam search, and the product is foam, it's not useful
                // for our filtering, so we just dismiss it. 
                // if ( !this.foam && result.isFoam() ) {
                //     return false; 
                // }

                for (const key in selections) {
                    if ( !result.optionMatches(key, selections[key]) ) {
                        return false; 
                    }
                }
                return true; 
            });
        },

        reloadPage() {
            // Removes selections from the URL
            const currentSelections = Storage.getSelections(store.collection);
            const queryString = new URLSearchParams(document.location.search);
            Object.keys(currentSelections).forEach(
                key => queryString.delete(key),
            );
            history.pushState(null, null, '?' + queryString.toString());

            Storage.removeSelections( store.collection ); 
            window.location.reload(); 
        },

        /**
         * Ignored selections are those that after filling all selections, these 
         * are left empty despite there are possible values to select from. 
         */
        getIgnoredSelections() {
            // nulled keys in selections. 
            let nulled = Object.keys(this.selections)
                .filter( key => this.selections[key] == null ); 

            // Results that match all the filled selections. 
            let temp_results = this.results.filter( result => {
                for(const key in this.selections) {
                    if ( this.selections[key] == null) continue; 

                    if ( !result.optionMatches(key, this.selections[key]) ) {
                        return false; 
                    }
                }

                return true; 
            }); 

            // this is key ignored if there are no results that would have a non empty 
            // value for that key. (A value that should have been selected via filtering)
            return nulled.filter( key => {
                return !temp_results.some( result => !result.optionIsEmpty(key)); 
            }); 
        },

        closeFilters() {
            this.$emit('closeFilters'); 
        }, 

        closePopup() {
            if (this.isDrawerFlow()) {
                this.$emit('closeDrawer');
                return;
            }

            if (this.popup) {
                const origin = sessionStorage.getItem('sf_origin');
                if (origin && origin.includes(window.location.host)) {
                    window.location.href = origin;
                } else if (document.referrer && document.referrer.includes(window.location.host)) {
                    window.location.href = document.referrer;
                } else {
                    if (window.history.length > 1) {
                        history.back();
                    } else {
                        window.location.href = '/';
                    }
                }
            } else {
                this.closeFilters();
            }
        }
    }
}
</script>
